import InputHandler from './input-handler.js';
import { base64PlayButton } from './play-button.js';
export class PlayerHtmlContainers {
    logger;
    containers = [];
    videoStream;
    dataChannel;
    onUserInteraction;
    constructor(logger) {
        this.logger = logger;
    }
    static setup(logger) {
        return new PlayerHtmlContainers(logger);
    }
    setVideoStream(stream) {
        this.videoStream = stream;
        this.containers.forEach((container) => {
            container.htmlVideoElement.srcObject = stream;
        });
    }
    setDataChannel(dataChannel) {
        this.dataChannel = dataChannel;
        this.containers.forEach((container) => {
            container.connectUiHandlers(dataChannel, () => this.onUserInteraction?.());
        });
    }
    addContainer(props) {
        if (this.containerAlreadyExists(props)) {
            this.logger.info('Container already exists. Skipping creation.', { props });
            return;
        }
        const htmlContainerElement = this.getHtmlElement(props);
        if (!htmlContainerElement) {
            this.logger.info('No HTML Container configured. Continuing without video.');
            return;
        }
        const container = Container.setup({
            logger: this.logger,
            displayMode: props.displayMode,
            parentElement: htmlContainerElement
        });
        this.containers.push(container);
        if (this.videoStream) {
            container.htmlVideoElement.srcObject = this.videoStream;
        }
        if (this.dataChannel) {
            container.connectUiHandlers(this.dataChannel, () => this.onUserInteraction?.());
        }
    }
    removeContainer(props) {
        const containerElement = this.getHtmlElement(props);
        const index = this.containers.findIndex((container) => container.parentElement === containerElement);
        if (index === -1) {
            this.logger.info('No container found to remove', { props });
            return;
        }
        this.logger.debug('Removing container', { props });
        const container = this.containers.splice(index, 1)[0];
        container.close();
    }
    containerAlreadyExists(props) {
        const containerElement = this.getHtmlElement(props);
        return this.containers.some((container) => container.parentElement === containerElement);
    }
    isEmpty() {
        return this.containers.length === 0;
    }
    close() {
        this.containers.forEach((container) => container.close());
        this.containers = [];
        this.videoStream = undefined;
        this.dataChannel = undefined;
        this.onUserInteraction = undefined;
    }
    getHtmlElement(props) {
        let parent = null;
        if (props.htmlContainerId) {
            parent = document.getElementById(props.htmlContainerId);
        }
        else if (props.htmlContainerReference) {
            parent = props.htmlContainerReference;
        }
        return parent;
    }
}
export class Container {
    logger;
    htmlContainerElement;
    htmlVideoElement;
    resizeObserver;
    inputHandler;
    parentElement;
    constructor(logger, htmlContainerElement, htmlVideoElement) {
        this.logger = logger;
        this.htmlContainerElement = htmlContainerElement;
        this.htmlVideoElement = htmlVideoElement;
    }
    static verifyContainerId(containerId) {
        const container = document.getElementById(containerId);
        if (!container) {
            throw new Error(`HTML element with id=${containerId} not found`);
        }
    }
    connectUiHandlers(dataChannel, onUserInteraction) {
        this.inputHandler = InputHandler.connect({
            videoElement: this.htmlVideoElement,
            dataChannel: dataChannel,
            logger: this.logger
        });
        this.inputHandler.on('userInteraction', () => onUserInteraction());
    }
    disconnectUiHandlers() {
        this.inputHandler?.disconnect();
        this.inputHandler?.removeAllListeners();
        this.inputHandler = undefined;
    }
    close() {
        this.logger.debug('Removing HTML elements');
        this.disconnectUiHandlers();
        this.htmlVideoElement.remove();
        this.htmlContainerElement.remove();
        this.resizeObserver?.disconnect();
    }
    static setup(props) {
        props.logger.debug('Setting up HTML elements');
        const { htmlContainerElement, htmlVideoElement } = createHtmlElements(props.logger);
        const container = new Container(props.logger, htmlContainerElement, htmlVideoElement);
        container.parentElement = props.parentElement;
        container.resizeObserver = setupDisplayMode(htmlContainerElement, htmlVideoElement, props.displayMode);
        embedUniqueElement(htmlContainerElement, props.parentElement);
        props.logger.debug('Finished setting up HTML elements');
        return container;
    }
}
function setupDisplayMode(htmlContainerElement, htmlVideoElement, displayMode = 'contain') {
    if (displayMode === 'cover') {
        // We don't use "object-fit: cover" for the video element, but instead hide the overflow on the container;
        // cover cuts off the video element while hiding the overflow leaves the video element intact.
        // This simplifies the calculation of mouse/touch events how the AVE expects it
        htmlContainerElement.style.display = 'flex';
        htmlContainerElement.style.flexDirection = 'column';
        htmlContainerElement.style.justifyContent = 'center';
        htmlContainerElement.style.overflow = 'hidden';
        htmlContainerElement.style.maxHeight = '100%';
        htmlContainerElement.style.maxWidth = '100%';
        return createResizeObserver(htmlContainerElement, htmlVideoElement);
    }
    htmlVideoElement.style.width = '100%';
    htmlVideoElement.style.height = '100%';
    htmlVideoElement.style.objectFit = 'contain';
    return undefined;
}
function createResizeObserver(htmlContainerElement, htmlVideoElement) {
    const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
            const { width, height } = entry.contentRect;
            if (width > 0 && height > 0 && width / height > 16 / 9) {
                htmlVideoElement.style.width = '100%';
                htmlVideoElement.style.height = 'auto';
                htmlContainerElement.style.flexDirection = 'column';
            }
            else {
                htmlVideoElement.style.width = 'auto';
                htmlVideoElement.style.height = '100%';
                htmlContainerElement.style.flexDirection = 'row';
            }
        }
    });
    resizeObserver?.observe(htmlContainerElement);
    return resizeObserver;
}
function createHtmlVideoElement() {
    const htmlVideoElement = document.createElement('video');
    htmlVideoElement.id = 'avp-webrtc-player-video';
    htmlVideoElement.style.display = 'block';
    htmlVideoElement.muted = true;
    htmlVideoElement.playsInline = true;
    htmlVideoElement.disablePictureInPicture = true;
    htmlVideoElement.autoplay = true;
    return htmlVideoElement;
}
function createHtmlContainerElement() {
    const htmlContainerElement = document.createElement('div');
    htmlContainerElement.id = 'webrtc-container';
    htmlContainerElement.style.width = '100%';
    htmlContainerElement.style.height = '100%';
    htmlContainerElement.style.position = 'relative';
    return htmlContainerElement;
}
function createHtmlElements(logger) {
    const htmlContainerElement = createHtmlContainerElement();
    const htmlVideoElement = createHtmlVideoElement();
    htmlContainerElement.appendChild(htmlVideoElement);
    const playButton = createPlayButtonOverlay(htmlVideoElement, logger);
    htmlContainerElement.appendChild(playButton);
    return { htmlContainerElement, htmlVideoElement };
}
function createPlayButtonOverlay(htmlVideoElement, logger) {
    const playButton = document.createElement('div');
    playButton.style.position = 'absolute';
    playButton.style.width = '100%';
    playButton.style.height = '100%';
    playButton.style.top = '0';
    playButton.style.zIndex = '2';
    playButton.style.alignItems = 'center';
    playButton.style.justifyContent = 'center';
    playButton.addEventListener('click', () => {
        void (async () => await htmlVideoElement.play())();
    });
    const playButtonImage = document.createElement('img');
    playButtonImage.src = `data:image/png;base64,${base64PlayButton}`;
    playButtonImage.style.height = '50%';
    playButton.style.display = 'none';
    const startPlayPromise = htmlVideoElement?.play();
    startPlayPromise
        ?.then(() => logger.debug('Successfully started stream'))
        .catch((error) => {
        if (error instanceof Error && error.name === 'NotAllowedError') {
            playButton.style.display = 'flex';
            logger.debug('Stream is suppressed by the device OS, displaying play button for manual start', error);
            htmlVideoElement?.addEventListener('play', () => (playButton.style.display = 'none'));
        }
        else {
            logger.error('An error occurred while starting the stream', { error });
        }
    });
    playButton.appendChild(playButtonImage);
    return playButton;
}
function embedUniqueElement(element, parentElement) {
    const existingContainer = parentElement.children.namedItem(element.id);
    if (existingContainer) {
        parentElement.replaceChild(element, existingContainer);
    }
    else {
        parentElement.appendChild(element);
    }
}
//# sourceMappingURL=player-html-container.js.map